import { useEffect, useRef } from 'react';
import { recordProgress } from '../../../services/readingProgress';
import { currentSectionId, writeSectionHash } from '../lib/articleSections';

/**
 * Watches how far down the article the reader has got and stores the furthest
 * point locally, so the home page can offer to resume it.
 *
 * Writes are throttled to once every few seconds and on unmount — scroll fires
 * far too often to touch localStorage on every frame.
 */
const WRITE_INTERVAL_MS = 4000;

/** Grace period so the arrival scroll finishes before the hash starts moving. */
const HASH_SETTLE_MS = 1200;

export function useTrackReading(params: {
  blogSlug?: string;
  blogTitle?: string;
  postSlug?: string;
  postTitle?: string;
  ready: boolean;
}) {
  const { blogSlug, blogTitle, postSlug, postTitle, ready } = params;
  const percentRef = useRef(0);
  const anchorRef = useRef<string | undefined>(undefined);
  const lastWriteRef = useRef(0);

  useEffect(() => {
    if (!ready || !blogSlug || !postSlug) return;

    percentRef.current = 0;
    anchorRef.current = undefined;
    lastWriteRef.current = 0;

    const save = () => {
      if (percentRef.current <= 0) return;
      recordProgress({
        blogSlug,
        blogTitle: blogTitle ?? blogSlug,
        postSlug,
        postTitle: postTitle ?? postSlug,
        percent: percentRef.current,
        anchor: anchorRef.current,
      });
    };

    const mountedAt = Date.now();

    /**
     * Keep the address bar pointing at the section being read, so a refresh,
     * a copied link or a browser bookmark all return to the same place.
     */
    const syncHash = (id: string | undefined) => {
      // The opening scroll is still settling; leave the hash alone so it does
      // not fight a deep link that has only just landed.
      if (Date.now() - mountedAt < HASH_SETTLE_MS) return;
      writeSectionHash(id);
    };

    const measure = () => {
      const article = document.querySelector('article');
      if (!article) return;

      const start = (article as HTMLElement).offsetTop;
      const height = (article as HTMLElement).clientHeight;
      if (height <= 0) return;

      const seen = window.scrollY + window.innerHeight - start;
      const percent = Math.min(100, Math.max(0, (seen / height) * 100));
      percentRef.current = Math.max(percentRef.current, percent);
      // Tracked every time, not only when the percentage advances: re-reading
      // an already-finished post must still update where you are now.
      const heading = currentSectionId();
      anchorRef.current = heading ?? anchorRef.current;
      syncHash(heading);

      const now = Date.now();
      if (now - lastWriteRef.current > WRITE_INTERVAL_MS) {
        lastWriteRef.current = now;
        save();
      }
    };

    let ticking = false;
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(() => {
        measure();
        ticking = false;
      });
    };

    measure();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
    // A closed tab never unmounts, so persist on the way out too.
    window.addEventListener('pagehide', save);

    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      window.removeEventListener('pagehide', save);
      save();
    };
  }, [ready, blogSlug, blogTitle, postSlug, postTitle]);
}
